import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector, batch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import Sticky from 'react-sticky-el';
import { useTranslation } from 'react-i18next';
import {
  fetchParticipantList,
  selectAllParticipants,
  ParticipantsSlice,
  fetchNonAttendeeParticipants,
} from 'models/participants';
import { UserSessionContext } from 'components/UserSession';
import { selectLocale } from 'models/localization';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import ParticipantListItem from './ParticipantListItem';

const RootContainer = styled.div`
  max-width: 860px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 16px auto 32px;
`;

const StyledStickyContainer = styled(Sticky)`
  width: calc(100% + 8px);
  z-index: 10;
  background-color: #F0F2F5;
`;

const HeaderText = styled.div`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.408px;
  text-transform: capitalize;
  color: #484848;
  align-self: flex-start;
  padding: 16px 4px;
  background-color: #F0F2F5;

  @media (max-width: 960px) {
    padding-left: 20px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-items: center;
`;

const FetchButton = styled(Button)`
  margin: 8px auto;
`;

const StyledPaper = withStyles(() => ({
  root: {
    width: '100%',
    paddingTop: '4px',
    paddingBottom: '8px',
  },
  rounded: {
    borderRadius: '8px',
  },
}))(Paper);

const ParticipantListContainer = ({ scrollableTarget }) => {
  const [isFetchError, setIsFetchError] = useState(false);
  const [isFetching, setIsFetch] = useState(false); // TODO: improve loading status with initialLoading from redux
  const { userSession: { role } } = useContext(UserSessionContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const participants = useSelector(selectAllParticipants);
  const {
    initialLoading,
    hasNextPage,
  } = useSelector((state) => state.participants);
  const locale = useSelector(selectLocale);
  const { pageNames, features } = useSelector((s) => s.eventConfigs);
  const fetchMore = useCallback(() => {
    setIsFetch(true);
    dispatch((features.includes('hideAttendees') && role === 'attendee') ? fetchNonAttendeeParticipants() : fetchParticipantList())
      .then((res) => {
        setIsFetch(false);
        if (Object.keys(res?.payload?.entities).length === 0) {
          setIsFetchError(true);
        }
      });
  }, [dispatch, features, role]);

  useEffect(() => {
    batch(() => {
      dispatch(ParticipantsSlice.actions.resetParticipants());
      fetchMore();
    });
  }, [dispatch, fetchMore]);

  useEffect(() => {
    if (isFetching) {
      setIsFetch(false);
    }
  }, [isFetching, participants.length]);

  const header = useMemo(() => pageNames.participant?.[locale] || t('page.participants', 'Participants'), [locale, pageNames.participant, t]);

  function handleRetry() {
    setIsFetch(true);
    setIsFetchError(false);
    fetchMore();
  }

  return (
    <RootContainer>
      <StyledStickyContainer
        scrollElement={scrollableTarget}
        stickyStyle={{ zIndex: 10 }}
      >
        <HeaderText>
          {header}
        </HeaderText>
      </StyledStickyContainer>
      <StyledPaper>
        {initialLoading && <CircularLoadingIndicator size={32} />}
        {
          !initialLoading && (
            <InfiniteScroll
              dataLength={participants.length}
              next={fetchMore}
              hasMore={hasNextPage}
              loader={<CircularLoadingIndicator key="loading-indicator" size={32} />}
              scrollableTarget={scrollableTarget}
              scrollThreshold={1}
              style={{ overflow: 'hidden' }}
            >
              {
                participants.map((participant) => {
                  const {
                    firstName,
                    lastName,
                    name,
                    uuid,
                    customAttendeeFields: {
                      vexpo_chat_tag: fallbackTag,
                      vexpo_chat_user_description: fallbackDescription,
                    },
                    vexpoChatTag,
                    vexpoChatUserDescription,
                  } = participant;
                  return (
                    <ParticipantListItem
                      key={uuid}
                      uuid={uuid}
                      name={name}
                      firstName={firstName}
                      lastName={lastName}
                      userTag={vexpoChatTag || fallbackTag}
                      userDescription={vexpoChatUserDescription || fallbackDescription}
                    />
                  );
                })
              }
            </InfiniteScroll>
          )
        }
        <ButtonRow>
          {hasNextPage && (
            <FetchButton
              variant="contained"
              color={isFetchError ? 'default' : 'primary'}
              onClick={handleRetry}
              disabled={initialLoading || isFetching}
            >
              {isFetchError ? 'retry' : 'next page'}
            </FetchButton>
          )}
        </ButtonRow>
      </StyledPaper>
    </RootContainer>
  );
};

ParticipantListContainer.propTypes = {
  scrollableTarget: PropTypes.instanceOf(Element),
};

ParticipantListContainer.defaultProps = {
  scrollableTarget: document.body,
};

export default ParticipantListContainer;
